// SyncProvider — initialises PowerSync at app startup and exposes
// sync status throughout the component tree via context.

import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { type PowerSyncDatabase } from '@powersync/web';
import { getPowerSyncDB, connectSync, getSyncStatus } from '@/lib/sync';

interface SyncState {
  db: PowerSyncDatabase | null;
  connected: boolean;
  lastSyncedAt: Date | null;
  hasSynced: boolean;
  syncing: boolean;
}

const SyncContext = createContext<SyncState>({
  db: null,
  connected: false,
  lastSyncedAt: null,
  hasSynced: false,
  syncing: false,
});

export function useSyncState() {
  return useContext(SyncContext);
}

export function SyncProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<SyncState>({
    db: null,
    connected: false,
    lastSyncedAt: null,
    hasSynced: false,
    syncing: false,
  });

  useEffect(() => {
    let mounted = true;
    const db = getPowerSyncDB();

    const init = async () => {
      await db.init();

      if (mounted) {
        setState(s => ({ ...s, db, syncing: true }));
      }

      // Attempt to connect (no-op if VITE_POWERSYNC_URL not set)
      try {
        await connectSync(db);
      } catch (err) {
        console.warn('[SyncProvider] Connection failed — local-only mode:', err);
      }

      // Poll sync status every 5s
      const poll = setInterval(async () => {
        if (!mounted) return;
        const status = await getSyncStatus(db);
        setState(s => ({
          ...s,
          db,
          connected: status.connected,
          lastSyncedAt: status.lastSyncedAt ?? null,
          hasSynced: status.hasSynced ?? false,
          syncing: false,
        }));
      }, 5000);

      return () => clearInterval(poll);
    };

    let cleanup: (() => void) | undefined;
    init().then(fn => { cleanup = fn; });

    return () => {
      mounted = false;
      cleanup?.();
      db.close();
    };
  }, []);

  return (
    <SyncContext.Provider value={state}>
      {children}
    </SyncContext.Provider>
  );
}
